import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Star, Trash2 } from "lucide-react";
import { useState } from "react";
import {
  SHARED_VIEW_PROTECTED_MESSAGE,
  isSharedViewIdentity,
  type SavedView,
} from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { LoadFailed } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { describeRejection } from "@/modules/settings/errors";
import { savedViewScopes } from "@/modules/settings/scopes";

const QUERY_KEY = ["settings", "views", "all"];

/**
 * Every saved view across every scope, keyed by which one it belongs to.
 *
 * One request per scope rather than one method that returns everything:
 * `SavedViewsRepository.list` only ever takes a single scope (it is what
 * backs one section's `SavedViewPicker`), so an admin view over all of them
 * has no choice but to ask once per scope and merge the answers itself.
 */
async function loadAllViews(
  list: (scope: string) => Promise<SavedView[]>,
): Promise<{ scope: string; views: SavedView[] }[]> {
  const scopes = savedViewScopes();
  const results = await Promise.all(
    scopes.map(async (scope) => ({ scope, views: await list(scope) })),
  );
  return results.filter((entry) => entry.views.length > 0);
}

/**
 * Saved views administration (M8-11).
 *
 * Unlike the rest of this module, `SavedViewsRepository` is fully wired —
 * every action below is a real write, not a preview. Renaming, re-defaulting
 * and deleting all need `settings.views.manage`; creating a view does not,
 * since anyone who can already see a module can save one for it from that
 * module's own toolbar. That asymmetry is why this screen offers no "new
 * view" button of its own — there is no filter, column set or sort to save
 * without a list open, so creation only ever happens from `SavedViewPicker`.
 */
export function SavedViewsAdmin() {
  const data = useDataSource();
  const queryClient = useQueryClient();

  const viewsQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => loadAllViews((scope) => data.savedViews.list(scope)),
  });

  // Hiding the rename/default/delete controls for someone without
  // `settings.views.manage` is a courtesy only — every one of those calls is
  // still checked server-side regardless of what this renders (FR-ACL-8,
  // NFR-14). A stale or wrong read here can make a button appear that a
  // write then correctly refuses.
  const currentUserQuery = useQuery({
    queryKey: ["current-user"],
    queryFn: () => data.currentUser(),
  });

  const [renaming, setRenaming] = useState<{
    id: string;
    draft: string;
  } | null>(null);
  const [problems, setProblems] = useState<Record<string, string>>({});
  const [pendingId, setPendingId] = useState<string | null>(null);

  function invalidate() {
    return queryClient.invalidateQueries({ queryKey: QUERY_KEY });
  }

  /** Drop a resolved view's rejection once a later call on it succeeds. */
  function clearProblem(id: string) {
    setProblems((prev) =>
      Object.fromEntries(
        Object.entries(prev).filter(([viewId]) => viewId !== id),
      ),
    );
  }

  async function rename(view: SavedView, name: string) {
    setPendingId(view.id);
    const result = await data.savedViews.update(view.id, { name });
    setPendingId(null);
    if (result.ok) {
      setRenaming(null);
      clearProblem(view.id);
      await invalidate();
    } else {
      setProblems((prev) => ({
        ...prev,
        [view.id]: describeRejection(result.rejection),
      }));
    }
  }

  async function setDefault(view: SavedView) {
    setPendingId(view.id);
    const result = await data.savedViews.setDefault(view.id);
    setPendingId(null);
    if (result.ok) {
      clearProblem(view.id);
      await invalidate();
    } else {
      setProblems((prev) => ({
        ...prev,
        [view.id]: describeRejection(result.rejection),
      }));
    }
  }

  async function remove(view: SavedView) {
    setPendingId(view.id);
    const result = await data.savedViews.delete(view.id);
    setPendingId(null);
    if (result.ok) {
      await invalidate();
    } else {
      setProblems((prev) => ({
        ...prev,
        [view.id]: describeRejection(result.rejection),
      }));
    }
  }

  if (viewsQuery.isPending) return <TableSkeleton rows={5} />;

  if (viewsQuery.isError) {
    return (
      <LoadFailed
        message={viewsQuery.error.message}
        onRetry={() => {
          void viewsQuery.refetch();
        }}
      />
    );
  }

  const canManage =
    currentUserQuery.data?.permissions.includes("settings.views.manage") ??
    false;

  if (viewsQuery.data.length === 0) {
    return (
      <p className="text-muted-foreground p-4 text-sm">
        No saved views exist in any scope yet. A view is created from a module's
        own toolbar, not from here.
      </p>
    );
  }

  return (
    <div className="max-w-3xl space-y-6">
      {viewsQuery.data.map(({ scope, views }) => (
        <div key={scope} className="space-y-2">
          <h3 className="text-sm font-semibold">{scope}</h3>
          <ul className="divide-border divide-y rounded-md border">
            {views.map((view) => {
              const isRenaming = renaming?.id === view.id;
              const isPending = pendingId === view.id;
              const problem = problems[view.id];
              // Comes with shipment tracking and is counted by the dashboard
              // (#527): its filter can change, it cannot be renamed or deleted.
              const builtIn = isSharedViewIdentity(view.scope, view.name);

              return (
                <li key={view.id} className="flex flex-col gap-1 px-3 py-2">
                  <div className="flex items-center gap-2">
                    {isRenaming ? (
                      <Input
                        autoFocus
                        className="h-8"
                        value={renaming.draft}
                        onChange={(event) => {
                          setRenaming({
                            id: view.id,
                            draft: event.target.value,
                          });
                        }}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" && renaming.draft.trim()) {
                            void rename(view, renaming.draft.trim());
                          }
                          if (event.key === "Escape") setRenaming(null);
                        }}
                      />
                    ) : (
                      <span className="flex-1 text-sm">{view.name}</span>
                    )}

                    {view.isShared ? (
                      <Badge variant="outline" className="font-normal">
                        Shared
                      </Badge>
                    ) : null}
                    {builtIn ? (
                      <Badge
                        variant="outline"
                        className="font-normal"
                        title={SHARED_VIEW_PROTECTED_MESSAGE}
                      >
                        Built in
                      </Badge>
                    ) : null}
                    {view.isDefault ? (
                      <Badge variant="secondary" className="font-normal">
                        Default
                      </Badge>
                    ) : null}

                    <Button
                      size="icon-sm"
                      variant="ghost"
                      aria-label={`Make ${view.name} the default`}
                      disabled={!canManage || view.isDefault || isPending}
                      onClick={() => {
                        void setDefault(view);
                      }}
                    >
                      <Star className={view.isDefault ? "fill-current" : ""} />
                    </Button>
                    <Button
                      size="icon-sm"
                      variant="ghost"
                      aria-label={`Rename ${view.name}`}
                      disabled={!canManage || isPending || builtIn}
                      onClick={() => {
                        setRenaming({ id: view.id, draft: view.name });
                      }}
                    >
                      <Pencil />
                    </Button>
                    <Button
                      size="icon-sm"
                      variant="ghost"
                      aria-label={`Delete ${view.name}`}
                      disabled={!canManage || isPending || builtIn}
                      onClick={() => {
                        void remove(view);
                      }}
                    >
                      <Trash2 />
                    </Button>
                  </div>
                  {problem ? (
                    <p className="text-destructive text-xs">{problem}</p>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      ))}

      {!canManage ? (
        <p className="text-muted-foreground text-xs">
          Renaming, re-defaulting and deleting need{" "}
          <code>settings.views.manage</code>, which the signed-in account does
          not hold — the controls above are disabled as a courtesy; the server
          would refuse the same action regardless.
        </p>
      ) : null}
    </div>
  );
}
